/**
 * Version history for any entity — one panel for all eleven types.
 *
 * Reads `GET /api/v1/entities/{id}/versions` and offers the two moves that are the same for every
 * type: open the next draft, and point the entity at a version. Naming a draft promotes it; naming
 * an older locked version is the rollback. There is no freeze button because freezing is not a
 * user action — activating a draft locks it on the way in.
 *
 * Drop it into any detail page that knows its entity id. Screens with richer, type-specific version
 * surfaces (agent templates: diffs + eval-gate test runs; workflow definitions: step-level restore)
 * keep those; this is the floor every type now has.
 */
import React, { useState } from 'react';
import { CheckCircle2, Clock, GitBranch, History, Lock, RotateCcw, Bot, Workflow as WorkflowIcon } from 'lucide-react';
import { Badge, Button, EmptyState, InlineConfirm } from '../ui/index.js';
import { usePolicy } from '../../hooks/usePolicy.js';
import { useActivateEntityVersion, useEntityVersions, useOpenEntityDraft } from '../../hooks/useEntityVersions.js';
import type { EntityVersion } from '../../services/api/entity-version.api.js';

/**
 * Mirrors `ENTITY_TYPE_POLICIES` in `server/src/api/routes/entity-versions.ts`. UI gating only —
 * the server evaluates the same policy and is the enforcement point.
 */
const WRITE_POLICY_BY_TYPE: Record<string, string> = {
  tool: 'CanManageSkills',
  skill: 'CanManageSkills',
  agent_template: 'CanManageAgentTemplates',
  workflow: 'CanManageWorkflows',
  workforce: 'CanManageWorkforce',
  evaluation_template: 'CanManageEvaluationTemplates',
  strategy: 'CanManageStrategies',
  strategy_template: 'CanManageStrategies',
  indicator: 'CanManageStrategies',
  bucket: 'CanManageStrategies',
  mutation_recipe: 'CanManageStrategies',
};

const LOCK_REASON_LABEL: Record<string, string> = {
  frozen: 'frozen',
  marketplace: 'listed',
  live_trading: 'live',
};

function AuthorLine({ version }: { version: EntityVersion }) {
  if (version.createdByWorkflowRunId !== null) {
    return (
      <span className="inline-flex items-center gap-1">
        <WorkflowIcon size={11} /> workflow run {version.createdByWorkflowRunId}
      </span>
    );
  }
  if (version.createdByDaemonRunId !== null) {
    return (
      <span className="inline-flex items-center gap-1">
        <Bot size={11} /> daemon run {version.createdByDaemonRunId}
      </span>
    );
  }
  return <span>user {version.createdByUserId ?? '—'}</span>;
}

function StateBadge({ version }: { version: EntityVersion }) {
  if (version.isActive) {
    return (
      <Badge variant="success">
        <CheckCircle2 size={11} /> active
      </Badge>
    );
  }
  if (version.state === 'draft') {
    return (
      <Badge variant="warning">
        <Clock size={11} /> draft
      </Badge>
    );
  }
  return (
    <Badge variant="neutral">
      <Lock size={11} /> {version.lockReason ? (LOCK_REASON_LABEL[version.lockReason] ?? version.lockReason) : 'locked'}
    </Badge>
  );
}

interface EntityVersionHistoryProps {
  entityId: number | undefined;
  /** Hide the "New draft" button on screens where the draft is opened by an editor instead. */
  allowOpenDraft?: boolean;
}

export const EntityVersionHistory: React.FC<EntityVersionHistoryProps> = ({ entityId, allowOpenDraft = true }) => {
  const [confirmId, setConfirmId] = useState<number | null>(null);
  const { data, isLoading, isError } = useEntityVersions(entityId);
  const activate = useActivateEntityVersion();
  const openDraft = useOpenEntityDraft();

  const entityType = data?.entity.type ?? '';
  const canManage = usePolicy(WRITE_POLICY_BY_TYPE[entityType] ?? 'CanManageSystem');

  if (!entityId || isLoading) return <div className="text-xs text-muted p-4">Loading versions…</div>;
  if (isError || !data) return <p className="text-xs text-danger p-4">Could not load version history.</p>;

  const versions = data.data;
  const acquired = data.entity.acquired;
  const hasDraft = versions.some((v) => v.state === 'draft');

  if (acquired) {
    return (
      <EmptyState
        icon={GitBranch}
        title="Acquired — no versions of its own"
        description="This entity points at a locked version published by another organization. Upgrade it to follow a newer one, or fork it to get an editable copy."
      />
    );
  }

  if (versions.length === 0) {
    return (
      <EmptyState icon={History} title="No versions yet" description="Nothing has been drafted for this entity." />
    );
  }

  const error = activate.error ?? openDraft.error;

  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between">
        <p className="text-sm font-medium text-heading">Versions</p>
        {allowOpenDraft && canManage && !hasDraft && (
          <Button
            size="sm"
            variant="ghost"
            loading={openDraft.isPending}
            onClick={() => openDraft.mutate({ entityId })}
          >
            <GitBranch size={12} /> New draft
          </Button>
        )}
      </div>

      {error && <p className="text-xs text-danger">{(error as Error).message}</p>}

      {versions.map((version) => {
        const isRollback = version.state === 'locked';
        return (
          <div key={version.id} className="border rounded-lg bg-surface-alt p-3 space-y-1">
            <div className="flex items-center justify-between gap-2">
              <div className="flex items-center gap-2">
                <span className="text-sm font-medium text-heading">v{version.version}</span>
                <StateBadge version={version} />
              </div>

              {canManage &&
                !version.isActive &&
                (confirmId === version.id ? (
                  <InlineConfirm
                    message={
                      isRollback
                        ? `Roll back to v${version.version}? It becomes the version this entity runs.`
                        : `Activate v${version.version}? It is frozen on the way in — editing means a new draft.`
                    }
                    confirmLabel={isRollback ? 'Roll back' : 'Activate'}
                    confirmVariant="warning"
                    loading={activate.isPending && activate.variables?.versionId === version.id}
                    onConfirm={() => {
                      activate.mutate({ entityId, versionId: version.id });
                      setConfirmId(null);
                    }}
                    onCancel={() => setConfirmId(null)}
                  />
                ) : (
                  <Button size="sm" variant={isRollback ? 'ghost' : 'success'} onClick={() => setConfirmId(version.id)}>
                    {isRollback ? (
                      <>
                        <RotateCcw size={12} /> Roll back
                      </>
                    ) : (
                      <>
                        <CheckCircle2 size={12} /> Activate
                      </>
                    )}
                  </Button>
                ))}
            </div>

            {version.changeSummary && <p className="text-xs text-body">{version.changeSummary}</p>}

            <div className="flex items-center gap-3 text-xs text-faint">
              <span>{new Date(version.createdAt).toLocaleString()}</span>
              <AuthorLine version={version} />
              {version.contentHash && <span className="font-mono">{version.contentHash.slice(0, 12)}…</span>}
            </div>
          </div>
        );
      })}
    </div>
  );
};
